import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Radio, Select, Space, Typography } from 'antd';

import NotifyPreviewButton from './NotifyPreviewButton';
import TermText from '../notifications/TermText';
import TokenInput from '../common/TokenInput';
import { COLLECTION_ROLE_OPTIONS } from '../../utils/permissions';
import { describeUnknownAudience } from '../notifications/notificationEditorModel';
import { getSharing } from '../../services/sharing';
import { normalizeId } from '../../utils/ids';

/**
 * The `notify` action's configuration, shared by both rule editors: event rules
 * (AutomationRuleEditor) and expected-item reminders (ExpectedItemRuleEditor).
 * The action's shape is identical on both — channels, audience, title, body —
 * and only the TOKEN vocabulary differs, so the tokens arrive as a prop
 * (utils/automationTokens.js builds them; see that file for why the two
 * vocabularies never borrow from each other).
 *
 * Audience options come from the collection's own sharing view — the same
 * groups and members the Sharing tab administers. Whatever is configured here
 * is still intersected server-side with each recipient's actual item access, so
 * this picker widens nothing. That is also why the role option reads "Everyone
 * with access ... at least <role>" rather than "everyone with a role": it names
 * a FLOOR, and everybody above it is included.
 *
 * Groups are MULTI. `{type: 'groups', group_ids: []}` is the canonical audience
 * shape; the single-group `{type: 'group', group_id}` that stored rules still
 * carry seeds into the same multi-select and saves back canonical, which the
 * API accepts either way (it normalizes on read forever).
 */

const { Text } = Typography;

const AUDIENCE_OPTIONS = [
	{ label: 'Groups', value: 'groups' },
	{ label: 'Specific people', value: 'users' },
	{ label: 'Everyone with access', value: 'role' },
];

// The people a USER element of the trigger item holds ("notify the
// assignee") — an EVENT rule's audience only: a schedule fires with no
// trigger item, so the option is offered where `userElements` are passed.
const ELEMENT_AUDIENCE_OPTION = { label: 'People in element…', value: 'element' };

// The platform's own words for collection access levels — the Sharing tab
// administers these exact labels, so the audience must not invent a second
// vocabulary for the same three levels.
const MIN_ROLE_OPTIONS = COLLECTION_ROLE_OPTIONS;

/**
 * The audience types THIS PANEL DRAWS, plus the legacy single-`group` shape it
 * seeds into the multi picker.
 *
 * ⚠ IT IS NOT THE PLATFORM'S LIST, and a type that is not on it is CARRIED,
 * never coerced. The API's registry (`keepr-api utils/automations/audience.js`
 * RESOLVERS) also holds `element-path` — *"whoever is in `manager` on the
 * record's `assignee`"*, dot-walking at most two lookups — and a later release
 * may add more. This used to fall back to `{type:'role', minRole:'read'}` and
 * then SAVE that, so opening somebody's "tell the manager" rule and pressing
 * Save quietly turned one person into every reader of the collection. A
 * widening nobody was told about is the worst thing a settings surface can do,
 * and it must not be possible to do it by accident.
 *
 * (The notification editor learned this first and keeps the same list under
 * `notificationEditorModel.EDITOR_AUDIENCE_TYPES`; both surfaces read and
 * write the same draft shape, so both carry it the same way.)
 */
const AUDIENCE_TYPES = [ 'groups', 'users', 'role', 'element' ];

/** The keys this panel owns on a stored notify action (unknown ones carry). */
export const KNOWN_NOTIFY_KEYS = [ 'type', 'channels', 'audience', 'title', 'body', 'ctas' ];

export const NOTIFY_CHANNEL_OPTIONS = [
	{ label: 'In-app', value: 'inApp' },
	{ label: 'Email', value: 'email' },
];

const userLabel = ( user, fallbackId ) => {
	const full = user?.fullName || `${ user?.firstName || '' } ${ user?.lastName || '' }`.trim();
	return full || fallbackId;
};

/** A blank panel: in-app to the whole collection, which is the safe default. */
export const emptyNotifyDraft = () => ( {
	channels: [ 'inApp' ],
	audienceType: 'role',
	groupIds: [],
	userIds: [],
	minRole: 'read',
	element: '',
	unknown: null,
	title: '',
	body: '',
} );

/**
 * A stored notify action → this panel's draft.
 *
 * ⚠ An ABSENT audience is not unknown, it is the DEFAULT: the resolver has
 * always read a missing audience as "everyone who can read it", which is the
 * role shape this seeds. A PRESENT type the panel does not draw rides out in
 * `unknown` and goes back byte for byte (see `AUDIENCE_TYPES`).
 */
export function notifyDraftFromAction( action ) {
	const audience = action?.audience || {};
	// The legacy single-group shape is a one-entry multi. Seeding it as such is
	// what makes an old rule editable at all — the alternative is a picker that
	// shows nothing while the rule visibly notifies a group.
	const storedGroupIds = audience.type === 'group'
		? [ audience.group_id ]
		: ( Array.isArray( audience.group_ids ) ? audience.group_ids : [] );
	const drawn = ! audience.type || audience.type === 'group' || AUDIENCE_TYPES.includes( audience.type );
	return {
		channels: Array.isArray( action?.channels ) && action.channels.length ? action.channels : [ 'inApp' ],
		audienceType: audience.type === 'group' ? 'groups'
			: ( drawn && audience.type ? audience.type : 'role' ),
		groupIds: storedGroupIds.map( normalizeId ).filter( Boolean ),
		userIds: ( Array.isArray( audience.userIds ) ? audience.userIds : [] ).map( normalizeId ).filter( Boolean ),
		minRole: audience.minRole || 'read',
		element: typeof audience.element === 'string' ? audience.element : '',
		unknown: drawn ? null : ( action?.audience || null ),
		title: action?.title || '',
		body: action?.body || '',
	};
}

export function buildNotifyAudience( draft ) {
	// A type this panel never rendered goes back exactly as it arrived.
	if ( draft?.unknown ) return { ...draft.unknown };
	if ( draft?.audienceType === 'groups' ) return { type: 'groups', group_ids: draft.groupIds || [] };
	if ( draft?.audienceType === 'users' ) return { type: 'users', userIds: draft.userIds || [] };
	if ( draft?.audienceType === 'element' ) return { type: 'element', element: draft.element || '' };
	return { type: 'role', minRole: draft?.minRole || 'read' };
}

/**
 * The documented notify JSON. Empty optionals are omitted rather than sent
 * blank — the API is strict about shapes and derives the rest.
 */
export function buildNotifyAction( draft, { extras = {}, ctas } = {} ) {
	const action = {
		...extras,
		type: 'notify',
		// Emitted as-checked, even empty: showing nothing checked while saving
		// "inApp" would be a lie; the API's non-empty-subset 400 surfaces.
		channels: Array.isArray( draft?.channels ) ? draft.channels : [ 'inApp' ],
		audience: buildNotifyAudience( draft ),
		title: ( draft?.title || '' ).trim(),
	};
	const body = ( draft?.body || '' ).trim();
	if ( body ) action.body = body;
	if ( ctas && ctas.length ) action.ctas = ctas;
	return action;
}

/**
 * Groups and members of one collection, shaped as Select options. Both editors
 * need the same list; the sharing endpoint is manage-gated exactly as the
 * automation routes are, so any manager reaching an editor can read it.
 */
export function useCollectionAudienceOptions( collectionId, enabled = true ) {
	const [ view, setView ] = useState( null );

	useEffect( () => {
		if ( ! collectionId || ! enabled ) return undefined;
		let active = true;
		// Promise.resolve-wrapped so a stubbed/failed call can never throw
		// synchronously out of an effect.
		Promise.resolve( getSharing( collectionId ) )
			.then( data => { if ( active ) setView( data || null ); } )
			// A failed sharing read leaves the pickers empty; the audience is
			// still typeable as a role, and the API validates the final shape.
			.catch( () => {} );
		return () => { active = false; };
	}, [ collectionId, enabled ] );

	return useMemo( () => {
		const groups = ( view?.groups || [] )
			.map( group => ( {
				value: normalizeId( group?._id ),
				label: `${ group?.name || 'Group' } (${ ( group?.members || [] ).length })`,
			} ) )
			.filter( option => option.value );

		// Members come from three places in one sharing view: the owner, every
		// group's membership, and the user grants. Deduped by id.
		const byId = new Map();
		const addUser = user => {
			const id = normalizeId( user?._id );
			if ( ! id || byId.has( id ) ) return;
			byId.set( id, userLabel( user, id ) );
		};
		addUser( view?.owner );
		( view?.groups || [] ).forEach( group => ( group?.members || [] ).forEach( addUser ) );
		( view?.grants || [] )
			.filter( grant => grant?.principalType === 'user' )
			.forEach( grant => addUser( grant.user || { _id: grant.principalId } ) );

		const users = [ ...byId.entries() ].map( ( [ value, label ] ) => ( { value, label } ) );
		return { groups, users };
	}, [ view ] );
}

/**
 * WHO hears about it, on its own: the radio of audience types and the picker
 * each one opens. Lifted out of the panel below so the notification editor
 * (components/notifications/NotificationEditorPage) renders the same four
 * controls, with the same words, without either surface owning a second copy
 * of them — a notification's audience and an automation's are the same idea
 * and must not drift into two vocabularies.
 *
 * It reads and writes the same draft the panel does; a caller that only needs
 * the audience simply ignores the rest of the draft's keys.
 */
export function NotifyAudienceFields( {
	draft,
	onChange,
	groups = [],
	users = [],
	userElements = null,
	label = 'Who hears about it',
} ) {

	const patch = next => onChange( { ...draft, ...next } );
	const offersElement = Array.isArray( userElements );
	const audienceOptions = offersElement ? [ ...AUDIENCE_OPTIONS, ELEMENT_AUDIENCE_OPTION ] : AUDIENCE_OPTIONS;

	// ⚠ NOT A PICKER SHOWING THE WRONG ANSWER. An audience this panel does not
	// draw reads, and it carries: drawing the nearest thing we do have would
	// save it back as that, and the nearest thing to "the manager" is "every
	// reader of the collection" (see `AUDIENCE_TYPES`).
	if ( draft?.unknown ) {
		return (
			<div>
				{label ? <Text strong className='d-block'>{label}</Text> : null}
				<Text type='secondary' className='d-block'>
					<TermText text={describeUnknownAudience( draft.unknown )} />
				</Text>
			</div>
		);
	}

	return (
		<Space direction='vertical' size={12} style={{ width: '100%' }}>

			<div>
				{label ? <Text strong className='d-block'>{label}</Text> : null}
				<Radio.Group
					options={audienceOptions}
					value={draft.audienceType}
					onChange={e => patch( { audienceType: e.target.value } )}
				/>
			</div>

			{draft.audienceType === 'element' ? (
				<div>
					<Select
						style={{ width: '100%' }}
						value={draft.element || undefined}
						aria-label='Element whose people to notify'
						placeholder={offersElement && userElements.length ? 'Pick a User element of the trigger card' : 'The trigger card has no User element'}
						options={offersElement ? userElements : []}
						disabled={! offersElement || ! userElements.length}
						onChange={element => patch( { element } )}
					/>
					<Text type='secondary' className='d-block' style={{ marginTop: 4 }}>
						Whoever the item names there (its assignee, say) hears about it.
					</Text>
				</div>
			) : null}

			{draft.audienceType === 'groups' ? (
				<Select
					mode='multiple'
					style={{ width: '100%' }}
					value={draft.groupIds}
					aria-label='Groups to notify'
					placeholder={groups.length ? 'Pick one or more groups' : 'No groups in this collection yet'}
					options={groups}
					onChange={groupIds => patch( { groupIds } )}
				/>
			) : null}

			{draft.audienceType === 'users' ? (
				<Select
					mode='multiple'
					style={{ width: '100%' }}
					value={draft.userIds}
					aria-label='People to notify'
					placeholder='Pick the people to notify'
					options={users}
					onChange={userIds => patch( { userIds } )}
				/>
			) : null}

			{draft.audienceType === 'role' ? (
				<Space>
					<Text type='secondary'>at least</Text>
					<Select
						style={{ minWidth: 180 }}
						value={draft.minRole}
						aria-label='Minimum role'
						options={MIN_ROLE_OPTIONS}
						onChange={minRole => patch( { minRole } )}
					/>
				</Space>
			) : null}

		</Space>
	);
}

export default function NotifyActionFields( {
	draft,
	onChange,
	tokens = [],
	groups = [],
	users = [],
	// Preview is opt-in: the reminder editor puts its own button beside Save
	// (one notify action, one obvious place for it), while an event rule can
	// carry several notify actions, so the button belongs on each panel.
	collectionId,
	buildPreviewRequest,
	// The trigger card's `user` elements, as Select options ({ value: name,
	// label }). Passed by the event rule editor only; absent, the element
	// audience is not offered (a schedule has nobody to read it off).
	userElements = null,
} ) {

	const patch = next => onChange( { ...draft, ...next } );

	return (
		<Space direction='vertical' size={12} style={{ width: '100%' }}>

			<div>
				<Text strong className='d-block'>Send by</Text>
				<Checkbox.Group
					options={NOTIFY_CHANNEL_OPTIONS}
					value={draft.channels}
					onChange={channels => patch( { channels } )}
				/>
			</div>

			<NotifyAudienceFields
				draft={draft}
				onChange={onChange}
				groups={groups}
				users={users}
				userElements={userElements}
			/>

			<div>
				<Text strong className='d-block'>Title</Text>
				<TokenInput
					value={draft.title}
					tokens={tokens}
					placeholder='Suzy has no med administration today'
					ariaLabel='Notification title'
					onChange={title => patch( { title } )}
				/>
			</div>

			<div>
				<Text strong className='d-block'>Message</Text>
				<TokenInput
					multiline
					value={draft.body}
					tokens={tokens}
					placeholder='Optional detail'
					ariaLabel='Notification message'
					onChange={body => patch( { body } )}
				/>
			</div>

			{buildPreviewRequest ? (
				<NotifyPreviewButton collectionId={collectionId} buildRequest={buildPreviewRequest} />
			) : null}

			<Text type='secondary'>
				Everyone chosen here is still filtered by what they can actually see — nobody is
				notified about an item they have no access to.
			</Text>

		</Space>
	);
}
